import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import Iconify from '../components/Iconify';
import Page from '../components/Page';

const ContactConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [configId, setConfigId] = useState(null);

  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    website: '',
    address: {
      organizationName: '',
      building: '',
      street: '',
      area: '',
      city: '',
      pincode: '',
      state: '',
      country: ''
    },
    mapEmbedUrl: '',
    videoEmbedUrl: '',
    socialLinks: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    },
    supportText: {
      emailResponse: '',
      phoneAvailability: ''
    }
  });

  useEffect(() => {
    fetchContactConfig();
  }, []);

  const fetchContactConfig = async () => {
    try {
      const response = await axios.get('https://cpccbe.org/api/contact/getcontactconfig');
      console.log('Fetched config:', response.data);
      setConfig(response.data);
      setConfigId(response.data._id);
      setFormData({
        phone: response.data.phone || '',
        email: response.data.email || '',
        website: response.data.website || '',
        address: {
          organizationName: response.data.address?.organizationName || '',
          building: response.data.address?.building || '',
          street: response.data.address?.street || '',
          area: response.data.address?.area || '',
          city: response.data.address?.city || '',
          pincode: response.data.address?.pincode || '',
          state: response.data.address?.state || '',
          country: response.data.address?.country || ''
        },
        mapEmbedUrl: response.data.mapEmbedUrl || '',
        videoEmbedUrl: response.data.videoEmbedUrl || '',
        socialLinks: {
          facebook: response.data.socialLinks?.facebook || '',
          twitter: response.data.socialLinks?.twitter || '',
          instagram: response.data.socialLinks?.instagram || '',
          linkedin: response.data.socialLinks?.linkedin || ''
        },
        supportText: {
          emailResponse: response.data.supportText?.emailResponse || '',
          phoneAvailability: response.data.supportText?.phoneAvailability || ''
        }
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contact config:', error);
      setMessage('No contact configuration found. You can create a new one.');
      setMessageType('info');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prevData) => ({
        ...prevData,
        [parent]: {
          ...prevData[parent],
          [child]: value
        }
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      if (configId) {
        // Update existing config
        const response = await axios.patch(
          `https://cpccbe.org/api/contact/updatecontactconfig/${configId}`,
          formData
        );
        setConfig(response.data);
        setMessage('Contact configuration updated successfully!');
        setMessageType('success');
      } else {
        // Create new config
        const response = await axios.post(
          'https://cpccbe.org/api/contact/createcontactconfig',
          formData
        );
        setConfig(response.data);
        setConfigId(response.data._id);
        setMessage('Contact configuration created successfully!');
        setMessageType('success');
      }
      setIsEditing(false);
      
      // Auto-hide message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving contact config:', error);
      setMessage('Error saving contact configuration. Please try again.');
      setMessageType('error');
    }
  };

  const handleCancel = () => {
    // Reset form data to original config
    if (config) {
      setFormData({
        phone: config.phone || '',
        email: config.email || '',
        website: config.website || '',
        address: config.address || {},
        mapEmbedUrl: config.mapEmbedUrl || '',
        videoEmbedUrl: config.videoEmbedUrl || '',
        socialLinks: config.socialLinks || {},
        supportText: config.supportText || {}
      });
    }
    setIsEditing(false);
    setMessage('');
  };

  if (loading) {
    return (
      <Page title="Contact Configuration">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Contact Configuration">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            Contact Configuration
          </Typography>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:edit-fill" />}
              onClick={() => setIsEditing(true)}
            >
              Edit Configuration
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:close-fill" />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:save-fill" />}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        {message && (
          <Alert severity={messageType} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Contact Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:phone-fill" />
                  Basic Contact Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:pin-fill" />
                  Address Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Organization Name"
                      name="address.organizationName"
                      value={formData.address.organizationName}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Building"
                      name="address.building"
                      value={formData.address.building}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Street"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Area"
                      name="address.area"
                      value={formData.address.area}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="City"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Pincode"
                      name="address.pincode"
                      value={formData.address.pincode}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="State"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Country"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>


          {/* Support Text */}
          <Grid item xs={12} >
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:message-circle-fill" />
                  Support Text
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email Response Time"
                      name="supportText.emailResponse"
                      value={formData.supportText.emailResponse}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                      placeholder="e.g., We'll respond within 24 hours"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Phone Availability"
                      name="supportText.phoneAvailability"
                      value={formData.supportText.phoneAvailability}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                      placeholder="e.g., Monday to Friday, 9AM-5PM"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:share-fill" />
                  Social Media Links
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Facebook"
                      name="socialLinks.facebook"
                      value={formData.socialLinks.facebook}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Twitter/X"
                      name="socialLinks.twitter"
                      value={formData.socialLinks.twitter}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Instagram"
                      name="socialLinks.instagram"
                      value={formData.socialLinks.instagram}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="LinkedIn"
                      name="socialLinks.linkedin"
                      value={formData.socialLinks.linkedin}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Map and Video URLs */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:link-fill" />
                  Embed URLs
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Google Maps Embed URL"
                      name="mapEmbedUrl"
                      value={formData.mapEmbedUrl}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                      multiline
                      rows={2}
                      helperText="Get embed URL from Google Maps Share → Embed a map"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="YouTube Video Embed URL"
                      name="videoEmbedUrl"
                      value={formData.videoEmbedUrl}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                      helperText="Format: https://www.youtube.com/embed/VIDEO_ID"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons at Bottom */}
        {isEditing && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Iconify icon="eva:close-fill" />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Iconify icon="eva:save-fill" />}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Container>
    </Page>
  );
};

export default ContactConfigPage;